import { useState } from 'react';
import { messageFrom } from '@bp-files/client';
import { FOLDER_COPY } from '@/components/FolderView/folderView.copy';
import { getDownload, type FileSummary } from '@/lib/api';

/**
 * FIL-8 / UPL-8: download is a request, not a space.
 *
 * The API validates the grant, writes the audit event, and only then hands back
 * a minutes-lived SAS — so the URL cannot exist until the click, and must not
 * outlive it. An `<a href>` holding one would put a credential in the DOM, in
 * the page's history, and in anything that scrapes either (SEC-3).
 *
 * **The blank tab opens before the `await`, not after.** `window.open` called
 * once the SAS has come back is outside the click's own synchronous call
 * stack, which iPad Safari's popup heuristics treat as no longer a user
 * gesture — it silently returns `null` instead of opening anything, with
 * nothing to catch and nothing to tell the reader. Desktop Chrome/Safari and
 * iPhone Safari are lenient about that gap; iPad's desktop-class Safari is
 * not. Opening a blank tab synchronously, inside the click, keeps the tab
 * inside the gesture — only its destination is set once the URL resolves.
 */
export function useFileDownload(
  file: FileSummary,
  onFailed: (message: string) => void,
  onStarted: () => void,
) {
  const [busy, setBusy] = useState(false);

  async function download() {
    setBusy(true);
    onStarted();

    const target = window.open('', '_blank');

    try {
      const { url } = await getDownload(file.id);
      if (target) {
        target.location.href = url;
      } else {
        // Already blocked — try the old path as a fallback rather than
        // silently doing nothing.
        window.open(url, '_blank');
      }
    } catch (caught) {
      target?.close();
      // The API's own message where there is one. A `429` names which cap was
      // reached and who can act on it (PLN-11) — for a reader at a plan-less
      // agency that is the
      // sending agency, which nothing on this screen could have worked out.
      onFailed(messageFrom(caught, FOLDER_COPY.download.failed));
    } finally {
      setBusy(false);
    }
  }

  return { busy, download };
}
